/**
 * The real notifications inbox: the header, `Mark all as read`, a
 * `Notification settings` link (ISSUE-1026), the four filter chips plus an
 * all-state, day-grouped rows, and `Load more` paging.
 *
 * Replaces ISSUE-326's placeholder body. The bell, its badge, and its poll
 * cadence belong to ISSUE-326 (`../shell/NotificationBell.tsx`,
 * `../shell/useShellBadges.ts`) and are not touched here: this screen reads
 * its own unread count from `useInboxGetUnreadCount`.
 */
import {
  useInboxGetUnreadCount,
  useInboxList,
  useInboxMarkAllRead,
  useInboxMarkRead,
} from "@sportsmet/notification-sdk/private";
import { Button, FOCUS_RING } from "@sportsmet/ui";
import {
  createFileRoute,
  Link,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { type ReactNode, useEffect, useRef, useState } from "react";

import { requireSignedIn } from "../auth/requireSignedIn";
import { extractErrorCode, showApiError } from "../errors/apiErrorBus";
import {
  resolveInboxDestination,
  type typeInboxDeepLink,
} from "../features/notifications/inboxDeepLink";
import {
  INBOX_FILTERS,
  NotificationFilterChips,
  type typeInboxCategory,
} from "../features/notifications/NotificationFilterChips";
import { NotificationInboxList } from "../features/notifications/NotificationInboxList";
import type { typeNotificationRowItem } from "../features/notifications/NotificationRow";
import { validateNotificationsSearch } from "../routeState/sectionSearch";
import { SectionScreen } from "../shell/SectionScreen";

type typeInboxListItem = typeNotificationRowItem & {
  alertKind: string;
  deepLink: typeInboxDeepLink;
};

const NotificationsScreen = (): ReactNode => {
  const search = useSearch({ from: "/notifications" });
  const navigate = useNavigate({ from: "/notifications" });

  const activeCategory: typeInboxCategory | null = INBOX_FILTERS.some(
    (filter) => filter.value === search.category,
  )
    ? (search.category as typeInboxCategory)
    : null;

  const [cursor, setCursor] = useState<string | null>(null);
  const [pages, setPages] = useState<typeInboxListItem[][]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  // The category this loaded `pages`/`cursor`/`nextCursor` state belongs to.
  // Comparing it against `activeCategory` DURING render, rather than
  // resetting from a `useEffect` keyed on `activeCategory`, matters: a
  // `useEffect` only runs after a render has already committed and painted.
  // If the reset lived there, a filter switch would paint once with the NEW
  // category's query already running but the OLD category's rows still in
  // `pages`, and only correct itself on the FOLLOWING paint. Resetting
  // synchronously in the render that detects the mismatch (React's
  // documented "adjusting state while rendering" pattern) folds the reset
  // into that same render, so the mismatched state is never committed.
  const [loadedCategory, setLoadedCategory] = useState(activeCategory);
  if (loadedCategory !== activeCategory) {
    setLoadedCategory(activeCategory);
    setCursor(null);
    setPages([]);
    setNextCursor(null);
  }

  const list = useInboxList({ category: activeCategory, cursor });
  const unread = useInboxGetUnreadCount({});

  // Both of these read the envelope they are handed before treating the write
  // as done. The generated SDK RESOLVES on a server refusal instead of
  // rejecting (`../queryClient.ts`), so a well formed `{ error, data: null }`
  // lands in `onSuccess` and react-query counts it as a win. Without the check
  // a fan taps `Mark all as read`, the rows stay bold, the bell keeps its
  // badge, and nothing says why. This is the SAME envelope read the list
  // already does with `showApiError(list.data)` below.
  //
  // A refusal is the only gap being closed here. A request that never became
  // an envelope at all, a dropped connection or a captive portal, still goes
  // to the `MutationCache` `onError` in `../queryClient.ts` and is already
  // covered.
  //
  // `showApiError` returns true when it raised the modal, so the refetches are
  // skipped on a refusal: the server changed nothing, and the generated hook's
  // own `invalidateRelated` still resyncs the list and the count either way.
  const markRead = useInboxMarkRead({
    onSuccess: (result) => {
      if (showApiError(result)) return;
      void unread.refetch();
      void list.refetch();
    },
  });
  const markAllRead = useInboxMarkAllRead({
    onSuccess: (result) => {
      if (showApiError(result)) return;
      void unread.refetch();
      void list.refetch();
    },
  });

  // Which `list.data` object this component has already folded into `pages`.
  // A plain `useEffect` on `[list.data]` has the same one-paint-late problem
  // the category reset above had, and it is WORSE here: react-query resolves
  // a cache hit (revisiting a category fetched within the last `staleTime`)
  // SYNCHRONOUSLY, in the same render that switched categories. An effect
  // would still make that render commit with the already-available data sitting
  // unread in `list.data` while `pages` (just reset above) is still empty, so
  // the fan would see the empty state flash before the effect runs a moment
  // later and the real rows replace it. Folding the merge into the render
  // itself, guarded by this ref so it runs once per distinct `list.data`
  // object, means a cache hit's rows are already in `pages` by the time
  // anything paints, cache hit or not.
  const mergedListDataRef = useRef<typeof list.data>(undefined);
  if (list.data !== undefined && list.data !== mergedListDataRef.current) {
    mergedListDataRef.current = list.data;
    const responseData = list.data.data;
    if (responseData) {
      const incoming = responseData.items as typeInboxListItem[];
      setPages((prev) => {
        // A `Map` keyed by `inboxItemId` both dedupes and merges: an id
        // already loaded keeps its ORDER position but takes the fresh copy
        // (so a refetch after mark-read / mark-all-read updates that row's
        // `isRead`, rather than the stale local copy winning forever), and a
        // genuinely new id is appended at the end, which is what "Load more"
        // needs. Re-setting an existing Map key does not move it, per the Map
        // spec, so insertion order is preserved for rows already on screen.
        const merged = new Map<string, typeInboxListItem>();
        for (const item of prev.flat()) merged.set(item.inboxItemId, item);
        for (const item of incoming) merged.set(item.inboxItemId, item);
        return [[...merged.values()]];
      });
      setNextCursor(responseData.nextCursor ?? null);
    }
  }

  // `showApiError` is a genuine side effect (it emits to a module-level
  // pub/sub the error-modal host subscribes to), not a state derivation, so
  // it stays in an effect rather than joining the render-time block above --
  // calling it directly during render could fire it twice for the same
  // response under React's development double-invoke or any future
  // concurrent re-render.
  useEffect(() => {
    const code = extractErrorCode(list.data);
    if (code !== null) showApiError(list.data, undefined, list.dataUpdatedAt);
  }, [list.dataUpdatedAt]);

  const seenIds = new Set<string>();
  const items: typeInboxListItem[] = [];
  for (const item of pages.flat()) {
    if (seenIds.has(item.inboxItemId)) continue;
    seenIds.add(item.inboxItemId);
    items.push(item);
  }

  const now = new Date();
  const unreadCount = unread.data?.data?.unreadCount ?? 0;
  const hasUnreadInScope = items.some((item) => !item.isRead);
  const doShowMarkAllAsRead = unreadCount > 0 && hasUnreadInScope;

  const handleOpen = ({ inboxItemId }: { inboxItemId: string }): void => {
    const item = items.find(
      (candidate) => candidate.inboxItemId === inboxItemId,
    );
    if (!item) return;

    markRead.mutate({ inboxItemIds: [inboxItemId] });
    setPages((prev) =>
      prev.map((page) =>
        page.map((pageItem) =>
          pageItem.inboxItemId === inboxItemId
            ? { ...pageItem, isRead: true }
            : pageItem,
        ),
      ),
    );

    const destination = resolveInboxDestination({
      deepLink: item.deepLink,
      alertKind: item.alertKind,
    });
    void navigate({ to: destination.to, search: destination.search });
  };

  return (
    <SectionScreen heading="Notifications">
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <NotificationFilterChips
            activeCategory={activeCategory}
            onSelect={({ category }) => {
              void navigate({
                to: "/notifications",
                search: (prev) => ({
                  ...prev,
                  category: category ?? undefined,
                }),
                replace: true,
              });
            }}
          />
          <div className="flex items-center gap-2">
            {doShowMarkAllAsRead ? (
              <Button
                type="button"
                variant="link"
                size="sm"
                onClick={() => {
                  markAllRead.mutate({ category: activeCategory });
                }}
              >
                Mark all as read
              </Button>
            ) : null}
            <Link
              to="/account/settings/notifications"
              className={`inline-flex min-h-touch-target items-center whitespace-nowrap px-3 text-caption text-primary underline underline-offset-4 hover:text-primary-hover ${FOCUS_RING}`}
              data-testid="notifications-settings-link"
            >
              Notification settings
            </Link>
          </div>
        </div>
        <NotificationInboxList
          items={items}
          isFirstPagePending={pages.length === 0 && list.isPending}
          now={now}
          onOpen={handleOpen}
        />
        {nextCursor ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => {
              setCursor(nextCursor);
            }}
          >
            Load more
          </Button>
        ) : null}
      </div>
    </SectionScreen>
  );
};

export const Route = createFileRoute("/notifications")({
  beforeLoad: requireSignedIn,
  validateSearch: validateNotificationsSearch,
  component: NotificationsScreen,
});
